import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { brokerageAgreementAPI, propertyAPI, contractAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import ConfirmModal from '../Common/ConfirmModal';
import './BrokerageAgreementCreate.css';

const BrokerageAgreementView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = false; // View mode is always read-only

  // Tab management
  const [activeTab, setActiveTab] = useState(1);
  const [tab1Saved, setTab1Saved] = useState(false);
  const [tab2Saved, setTab2Saved] = useState(false);
  const [tab3Saved, setTab3Saved] = useState(false);
  const [tab4Saved, setTab4Saved] = useState(false);
  const [tab5Saved, setTab5Saved] = useState(false);

  // Brokerage Agreement data
  const [brokerageAgreementId, setBrokerageAgreementId] = useState(null);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [propertyId, setPropertyId] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);

  // Tab 2: Units
  const [units, setUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  // Tab 3: Parties
  const [lessor, setLessor] = useState(null);
  const [organizationId, setOrganizationId] = useState('');
  const [propertyOwnerships, setPropertyOwnerships] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  // Tab 4: Agreement Scope
  const [selectedScopes, setSelectedScopes] = useState([]); // ["RentCollection", "UnitsLeasing", "UnitsMarketing"]

  // Tab 5: Additional Terms
  const [defaultTermsAr, setDefaultTermsAr] = useState('');
  const [defaultTermsEng, setDefaultTermsEng] = useState('');
  const [additionalTermsAr, setAdditionalTermsAr] = useState('');
  const [additionalTermsEng, setAdditionalTermsEng] = useState('');

  // Dropdowns
  const [dropdowns, setDropdowns] = useState({
    nationalities: [],
    identities: [],
    countries: [],
    cities: [],
    areas: [],
    districts: [],
    banks: [],
    unitFacilities: []
  });

  // Modals
  const [showAddLessorModal, setShowAddLessorModal] = useState(false);
  const [showUnitFacilitiesModal, setShowUnitFacilitiesModal] = useState(false);
  const [currentUnitIndex, setCurrentUnitIndex] = useState(-1);

  // Lessor form data
  const [lessorFormData, setLessorFormData] = useState({
    agentNumber: '',
    nameAr: '',
    nameEng: '',
    dob: '',
    nationalityId: '',
    identityTypeId: '',
    identityNumber: '',
    mobileCountryCode: '',
    mobileNumber: '',
    emailId: '',
    bankId: '',
    bankAccountNumber: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    buildingNumber: '',
    additionalNumber: '',
    postalCode: ''
  });

  // Unit facilities selection
  const [selectedUnitFacilities, setSelectedUnitFacilities] = useState({});

  // Supporting data
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load dropdowns on mount
  useEffect(() => {
    loadDropdowns();
    loadOrganizations();
    loadDefaultTerms();
  }, []);

  // Check for selected property from PropertySelect page
  useEffect(() => {
    const selectedPropertyIdFromStorage = sessionStorage.getItem('selectedPropertyId');
    if (selectedPropertyIdFromStorage) {
      const propertyId = parseInt(selectedPropertyIdFromStorage);
      if (!isNaN(propertyId) && propertyId > 0) {
        handleSelectPropertyById(propertyId);
      }
      sessionStorage.removeItem('selectedPropertyId');
    }
  }, []);

  // Load agreement data for viewing
  useEffect(() => {
    if (id) {
      loadBrokerageAgreement();
    }
  }, [id]);

  const loadDropdowns = async () => {
    try {
      const response = await propertyAPI.getDropdowns();
      const data = response.data || {};
      setDropdowns({
        nationalities: data.nationalities || [],
        identities: data.identities || [],
        countries: data.countries || [],
        cities: data.cities || [],
        areas: data.areas || [],
        districts: data.districts || [],
        banks: data.banks || [],
        unitFacilities: data.unitFacilities || []
      });
    } catch (err) {
      console.error('Failed to load dropdowns:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await brokerageAgreementAPI.getOrganizations();
      setOrganizations(response.data || []);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const loadDefaultTerms = async () => {
    try {
      const response = await brokerageAgreementAPI.getDefaultTerms();
      const data = response.data || {};
      setDefaultTermsAr(data.defaultTermsAr || '');
      setDefaultTermsEng(data.defaultTermsEng || '');
    } catch (err) {
      console.error('Failed to load default terms:', err);
    }
  };

  const loadBrokerageAgreement = async () => {
    try {
      setLoading(true);
      const response = await brokerageAgreementAPI.getById(id);
      const agreement = response.data || {};

      // Tab 1 data
      setBrokerageAgreementId(agreement.id || agreement.Id);
      setDateFrom(agreement.dateFrom ? new Date(agreement.dateFrom).toISOString().split('T')[0] : '');
      setDateTo(agreement.dateTo ? new Date(agreement.dateTo).toISOString().split('T')[0] : '');
      setPropertyId(agreement.propertyId || agreement.PropertyId || null);

      // Load property details
      if (agreement.propertyId || agreement.PropertyId) {
        await loadSelectedProperty(agreement.propertyId || agreement.PropertyId);
      }

      // Tab 2: Units
      if (agreement.brokerageAgreementUnits || agreement.BrokerageAgreementUnits) {
        const agreementUnits = agreement.brokerageAgreementUnits || agreement.BrokerageAgreementUnits;
        setSelectedUnits(agreementUnits.map(au => ({
          propertyUnitId: au.propertyUnitId || au.PropertyUnitId,
          agreementStatus: au.agreementStatus !== false && au.agreementStatus !== 'false',
          facilities: (au.brokerageAgreementUnitFacilities || au.BrokerageAgreementUnitFacilities || []).map(f => ({
            FacilityId: f.facilityId || f.FacilityId,
            Quantity: f.quantity || f.Quantity || 1
          }))
        })));
        setTab2Saved(true);
      }

      // Tab 3: Parties
      if (agreement.brokerageAgreementLessor || agreement.BrokerageAgreementLessor) {
        const lessorData = agreement.brokerageAgreementLessor || agreement.BrokerageAgreementLessor;
        setLessor({ propertyOwnershipId: lessorData.propertyOwnershipId || lessorData.PropertyOwnershipId });
      }
      if (agreement.brokerageAgreementOrganization || agreement.BrokerageAgreementOrganization) {
        const orgData = agreement.brokerageAgreementOrganization || agreement.BrokerageAgreementOrganization;
        setOrganizationId(String(orgData.organizationId || orgData.OrganizationId || ''));
      }

      // Tab 4: Scopes
      if (agreement.brokerageAgreementScopes || agreement.BrokerageAgreementScopes) {
        const scopes = agreement.brokerageAgreementScopes || agreement.BrokerageAgreementScopes;
        setSelectedScopes(scopes.map(s => s.scopeType || s.ScopeType || '').filter(s => s));
      }

      // Tab 5: Terms
      if (agreement.brokerageAgreementTerms || agreement.BrokerageAgreementTerms) {
        const terms = agreement.brokerageAgreementTerms || agreement.BrokerageAgreementTerms;
        setAdditionalTermsAr(terms.additionalTermsAr || terms.AdditionalTermsAr || '');
        setAdditionalTermsEng(terms.additionalTermsEng || terms.AdditionalTermsEng || '');
      }

      setTab1Saved(true);
      if (agreement.brokerageAgreementUnits || agreement.BrokerageAgreementUnits) setTab2Saved(true);
      if ((agreement.brokerageAgreementLessor || agreement.BrokerageAgreementLessor) && 
          (agreement.brokerageAgreementOrganization || agreement.BrokerageAgreementOrganization)) {
        setTab3Saved(true);
      }
    } catch (err) {
      console.error('Failed to load brokerage agreement:', err);
      setErrorMessage(t('brokerageAgreement.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedProperty = async (propId) => {
    try {
      const response = await brokerageAgreementAPI.getPropertiesWithDetails({ propertyId: parseInt(propId) });
      const data = response.data || {};
      let properties = Array.isArray(data) ? data : (data.properties || data.items || []);
      
      if (properties.length > 0) {
        setSelectedProperty(properties[0]);
        setPropertyId(parseInt(propId));
        await loadPropertyUnits(propId);
        await loadPropertyOwnerships(propId);
      }
    } catch (err) {
      console.error('Failed to load property:', err);
    }
  };

  const loadPropertyUnits = async (propId) => {
    try {
      const response = await brokerageAgreementAPI.getPropertyUnits(propId);
      setUnits(response.data || []);
    } catch (err) {
      console.error('Failed to load property units:', err);
    }
  };

  const loadPropertyOwnerships = async (propId) => {
    try {
      const response = await brokerageAgreementAPI.getPropertyOwnerships(propId);
      setPropertyOwnerships(response.data || []);
    } catch (err) {
      console.error('Failed to load property ownerships:', err);
    }
  };

  const handleSelectPropertyById = async (propId) => {
    await loadSelectedProperty(propId);
  };

  // Tab navigation validation - all tabs accessible in view mode
  const canAccessTab = (tabNumber) => {
    return true; // All tabs accessible in view mode
  };

  const handleTabClick = (tabNumber) => {
    if (canAccessTab(tabNumber)) {
      setActiveTab(tabNumber);
    }
  };

  // Tab 1: Save Brokerage Agreement
  const handleSaveTab1 = async () => {
    if (!dateFrom || !dateTo) {
      setErrorMessage('Date From and Date To are required.');
      setShowErrorModal(true);
      return;
    }
    
    if (!propertyId) {
      setErrorMessage('Please select a property.');
      setShowErrorModal(true);
      return;
    }
    
    if (new Date(dateTo) < new Date(dateFrom)) {
      setErrorMessage('Date To must be after Date From.');
      setShowErrorModal(true);
      return;
    }

    try {
      setLoading(true);
      const data = {
        id: brokerageAgreementId || 0,
        dateFrom,
        dateTo,
        propertyId
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreement(data);
      
      if (response.data.success) {
        setBrokerageAgreementId(response.data.brokerageAgreementId);
        setTab1Saved(true);
        setSuccessMessage('Brokerage Agreement saved successfully.');
        setShowSuccessModal(true);
        
        await loadPropertyUnits(propertyId);
        await loadPropertyOwnerships(propertyId);
      } else {
        setErrorMessage(response.data.message || 'Failed to save brokerage agreement.');
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to save brokerage agreement. Please try again.');
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Tab 2: Save Units
  const handleSaveTab2 = async () => {
    if (!brokerageAgreementId) {
      setErrorMessage('Please save the brokerage agreement first (Tab 1).');
      setShowErrorModal(true);
      return;
    }

    if (selectedUnits.length === 0) {
      setErrorMessage('Please select at least one unit.');
      setShowErrorModal(true);
      return;
    }

    try {
      setLoading(true);
      const data = {
        brokerageAgreementId: parseInt(brokerageAgreementId),
        brokerageAgreementUnits: selectedUnits.map(unit => ({
          propertyUnitId: parseInt(unit.propertyUnitId),
          agreementStatus: unit.agreementStatus === true || unit.agreementStatus === 'true',
          facilities: (unit.facilities || []).map(f => ({
            FacilityId: parseInt(f.FacilityId || f.facilityId),
            Quantity: parseInt(f.Quantity || f.quantity || 1)
          }))
        }))
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreementUnits(data);
      
      if (response.data.success) {
        setTab2Saved(true);
        setSuccessMessage('Brokerage Agreement units saved successfully.');
        setShowSuccessModal(true);
      } else {
        setErrorMessage(response.data.message || 'Failed to save units.');
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to save units. Please try again.');
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Tab 3: Save Parties
  const handleSaveTab3 = async () => {
    if (!brokerageAgreementId) {
      setErrorMessage('Please save the brokerage agreement first (Tab 1).');
      setShowErrorModal(true);
      return;
    }

    if (!lessor) {
      setErrorMessage('Please select a lessor.');
      setShowErrorModal(true);
      return;
    }

    if (!organizationId) {
      setErrorMessage('Please select an organization (Second Party).');
      setShowErrorModal(true);
      return;
    }

    try {
      setLoading(true);
      const data = {
        brokerageAgreementId: parseInt(brokerageAgreementId),
        contractLessor: lessor ? { propertyOwnershipId: parseInt(lessor.propertyOwnershipId) } : null,
        organizationId: parseInt(organizationId)
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreementParties(data);
      
      if (response.data.success) {
        setTab3Saved(true);
        setSuccessMessage('Brokerage Agreement parties saved successfully.');
        setShowSuccessModal(true);
      } else {
        setErrorMessage(response.data.message || 'Failed to save parties.');
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to save parties. Please try again.');
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Tab 4: Save Agreement Scope
  const handleSaveTab4 = async () => {
    if (!brokerageAgreementId) {
      setErrorMessage('Please save the brokerage agreement first (Tab 1).');
      setShowErrorModal(true);
      return;
    }

    if (selectedScopes.length === 0) {
      setErrorMessage('Please select at least one agreement scope.');
      setShowErrorModal(true);
      return;
    }

    try {
      setLoading(true);
      const data = {
        brokerageAgreementId: parseInt(brokerageAgreementId),
        scopeTypes: selectedScopes // ["RentCollection", "UnitsLeasing", "UnitsMarketing"]
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreementScope(data);
      
      if (response.data.success) {
        setTab4Saved(true);
        setSuccessMessage('Agreement scope saved successfully.');
        setShowSuccessModal(true);
      } else {
        setErrorMessage(response.data.message || 'Failed to save agreement scope.');
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to save agreement scope. Please try again.');
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Tab 5: Save Additional Terms
  const handleSaveTab5 = async () => {
    if (!brokerageAgreementId) {
      setErrorMessage('Please save the brokerage agreement first (Tab 1).');
      setShowErrorModal(true);
      return;
    }

    try {
      setLoading(true);
      const data = {
        brokerageAgreementId: parseInt(brokerageAgreementId),
        defaultTermsAr,
        defaultTermsEng,
        additionalTermsAr,
        additionalTermsEng
      };
      
      const response = await brokerageAgreementAPI.saveBrokerageAgreementTerms(data);
      
      if (response.data.success) {
        setTab5Saved(true);
        setSuccessMessage('Additional terms saved successfully.');
        setShowSuccessModal(true);
      } else {
        setErrorMessage(response.data.message || 'Failed to save additional terms.');
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to save additional terms. Please try again.');
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Next Tab
  const handleNextTab = async () => {
    if (activeTab === 1) {
      if (!tab1Saved) {
        await handleSaveTab1();
      }
      if (tab1Saved || brokerageAgreementId) {
        setActiveTab(2);
      }
    } else if (activeTab === 2) {
      if (!tab2Saved) {
        await handleSaveTab2();
      }
      if (tab2Saved) {
        setActiveTab(3);
      }
    } else if (activeTab === 3) {
      if (!tab3Saved) {
        await handleSaveTab3();
      }
      if (tab3Saved) {
        setActiveTab(4);
      }
    } else if (activeTab === 4) {
      if (!tab4Saved) {
        await handleSaveTab4();
      }
      if (tab4Saved) {
        setActiveTab(5);
      }
    }
  };

  // Handle Previous Tab
  const handlePreviousTab = () => {
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
    }
  };

  // Handle Property Selection
  const handleSelectProperty = () => {
    navigate('/property/select', { state: { returnUrl: '/brokerage-agreement/create', brokerageAgreementId } });
  };

  // Handle Scope Toggle
  const handleScopeToggle = (scopeType) => {
    if (selectedScopes.includes(scopeType)) {
      setSelectedScopes(selectedScopes.filter(s => s !== scopeType));
    } else {
      setSelectedScopes([...selectedScopes, scopeType]);
    }
  };

  // Create Property Ownership (for Add New Lessor)
  const createPropertyOwnership = async (ownershipData) => {
    try {
      // Use contractAPI since both Contract and BrokerageAgreement share the same endpoint
      const response = await contractAPI.createPropertyOwnership(ownershipData);
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const scopeOptions = [
    { value: 'RentCollection', label: 'Rent Collection' },
    { value: 'UnitsLeasing', label: 'Units Leasing' },
    { value: 'UnitsMarketing', label: 'Units Marketing' }
  ];

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 className="page-title">
          {t('brokerageAgreement.create.viewTitle')}
        </h2>
      </div>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1.5rem' }}>
          <button className="btn btn-secondary me-2" onClick={() => navigate('/brokerage-agreement')}>
            <i className="fas fa-arrow-left"></i> {t('brokerageAgreement.view.backToList')}
          </button>
          <button className="btn btn-primary" onClick={() => navigate(`/brokerage-agreement/edit/${id}`)}>
            <i className="fas fa-edit"></i> {t('brokerageAgreement.view.edit')}
          </button>
        </div>

      <div className="property-form-container">
        {/* Tabs Navigation */}
        <div className="property-tabs">
          <div 
            className={`tab-header ${activeTab === 1 ? 'active' : ''} ${!canAccessTab(1) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(1)}
          >
            <i className="fas fa-building"></i>
            <span>{t('brokerageAgreement.view.tabs.tab1')}</span>
            {tab1Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 2 ? 'active' : ''} ${!canAccessTab(2) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(2)}
          >
            <i className="fas fa-door-open"></i>
            <span>{t('brokerageAgreement.view.tabs.tab2')}</span>
            {tab2Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 3 ? 'active' : ''} ${!canAccessTab(3) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(3)}
          >
            <i className="fas fa-users"></i>
            <span>{t('brokerageAgreement.view.tabs.tab3')}</span>
            {tab3Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 4 ? 'active' : ''} ${!canAccessTab(4) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(4)}
          >
            <i className="fas fa-list-check"></i>
            <span>{t('brokerageAgreement.view.tabs.tab4')}</span>
            {tab4Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
          <div 
            className={`tab-header ${activeTab === 5 ? 'active' : ''} ${!canAccessTab(5) ? 'disabled' : ''}`} 
            onClick={() => handleTabClick(5)}
          >
            <i className="fas fa-file-contract"></i>
            <span>{t('brokerageAgreement.view.tabs.tab5')}</span>
            {tab5Saved && <i className="fas fa-check-circle text-success ms-2"></i>}
          </div>
        </div>

        {/* Tab Content */}
        <div className="property-tab-content">
          {/* Tab 1: Select Property - Same as Contract */}
          {activeTab === 1 && (
            <div className="tab-panel">
              <h3 className="tab-title">Select Property</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> Date From <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      required
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> Date To <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      required
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header">
                  <h5><i className="fas fa-building text-primary"></i> <span className="text-primary">Select Property</span></h5>
                </div>
                <div className="card-body">
                  {selectedProperty ? (
                    <div className="property-display-card">
                      <div className="row align-items-center">
                        <div className="col-md-10">
                          <div className="row mb-2">
                            <div className="col-12">
                              <h5 className="mb-1" style={{ display: 'inline-block', marginRight: '0.5rem' }}>
                                {selectedProperty.nameEng || selectedProperty.NameEng}
                              </h5>
                              {(selectedProperty.nameAr || selectedProperty.NameAr) && (
                                <span className="text-muted small">
                                  - {selectedProperty.nameAr || selectedProperty.NameAr}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-12 d-flex flex-wrap gap-3 align-items-center">
                              {(selectedProperty.code || selectedProperty.Code) && (
                                <span className="small">
                                  <strong>Code:</strong> {selectedProperty.code || selectedProperty.Code}
                                </span>
                              )}
                              {selectedProperty.realEstateType && (
                                <span className="small">
                                  <strong>Type:</strong> {selectedProperty.realEstateType.nameEng || selectedProperty.realEstateType.NameEng}
                                </span>
                              )}
                              {selectedProperty.city && (
                                <span className="small">
                                  <strong>City:</strong> {selectedProperty.city.cityNameEng || selectedProperty.city.CityNameEng}
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedProperty.propertyFacilities && Array.isArray(selectedProperty.propertyFacilities) && selectedProperty.propertyFacilities.length > 0 && (
                            <div className="row mt-3">
                              <div className="col-12">
                                <label className="form-label facilities-section-label" style={{ marginBottom: '1rem' }}>
                                  <i className="fas fa-star"></i> Facilities & Amenities
                                </label>
                                <div className="facilities-section-container">
                                  <div className="facilities-grid">
                                    {selectedProperty.propertyFacilities.map((facility, idx) => (
                                      <div key={idx} className="facility-card active" style={{ cursor: 'default' }}>
                                        <div className="facility-card-content">
                                          <div className="facility-icon">
                                            <i className="fas fa-check-circle"></i>
                                          </div>
                                          <div className="facility-info">
                                            <div className="facility-name">{facility.nameEng || facility.NameEng || facility}</div>
                                            {facility.nameAr || facility.NameAr ? (
                                              <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="col-md-2 text-center text-md-end">
                          <span style={{ display: 'none' }}></span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle"></i> No property selected.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Select Units - Same as Contract */}
          {activeTab === 2 && (
            <div className="tab-panel">
              <h3 className="tab-title">Select Units</h3>
              <div className="card mb-4">
                <div className="card-header">
                  <h5>
                    <i className="fas fa-door-open"></i>
                    <span>Select Units</span>
                  </h5>
                </div>
                <div className="card-body">
                  {!propertyId ? (
                    <div className="alert alert-warning">
                      <i className="fas fa-exclamation-triangle"></i> Please select a property first.
                    </div>
                  ) : units.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle"></i> No units available for this property.
                    </div>
                  ) : (
                    <div className="units-grid">
                      {units.map((unit) => {
                        const unitId = unit.propertyUnitId || unit.id || unit.Id;
                        const isSelected = selectedUnits.some(su => su.propertyUnitId === unitId);
                        const selectedUnit = selectedUnits.find(su => su.propertyUnitId === unitId);
                        return (
                          <div
                            key={unitId}
                            className={`unit-card ${isSelected ? 'selected' : ''}`}
                          >
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                disabled
                                readOnly
                              />
                              <label className="form-check-label">
                                <strong>Unit {unit.unitNumber || unit.UnitNumber || unitId}</strong>
                                {(unit.unitType || unit.UnitType) && (
                                  <span className="text-muted"> - {(unit.unitType?.nameEng || unit.UnitType?.NameEng)}</span>
                                )}
                              </label>
                            </div>
                            <div className="mb-2 d-flex gap-3 align-items-center" style={{ flexWrap: 'nowrap' }}>
                              {(unit.unitArea || unit.UnitArea) && (
                                <span className="small" style={{ whiteSpace: 'nowrap' }}>
                                  <strong>Area:</strong> {unit.unitArea || unit.UnitArea} m²
                                </span>
                              )}
                              {(unit.unitUsage || unit.UnitUsage) && (
                                <span className="small" style={{ whiteSpace: 'nowrap' }}>
                                  <strong>Usage:</strong> {(unit.unitUsage?.nameEng || unit.UnitUsage?.NameEng)}
                                </span>
                              )}
                            </div>
                            {unit.facilities && Array.isArray(unit.facilities) && unit.facilities.length > 0 && (
                              <div className="mt-3 mb-2">
                                <label className="form-label facilities-section-label" style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
                                  <i className="fas fa-star"></i> Facilities & Amenities
                                </label>
                                <div className="facilities-section-container" style={{ padding: '1rem' }}>
                                  <div className="facilities-grid">
                                    {unit.facilities.map((facility, idx) => (
                                      <div key={idx} className="facility-card active" style={{ cursor: 'default' }}>
                                        <div className="facility-card-content">
                                          <div className="facility-icon">
                                            <i className="fas fa-check-circle"></i>
                                          </div>
                                          <div className="facility-info">
                                            <div className="facility-name">{facility.nameEng || facility.NameEng || facility}</div>
                                            {facility.nameAr || facility.NameAr ? (
                                              <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                            {isSelected && (
                              <div className="mt-2">
                                <label className="form-check-label small">
                                  <input
                                    type="checkbox"
                                    checked={selectedUnit?.agreementStatus || false}
                                    onChange={() => {}}
                                    disabled
                                    readOnly
                                  />
                                  {' '}Active Agreement
                                </label>
                                <span className="small text-muted mt-2">
                                  <i className="fas fa-star"></i> Facilities: {selectedUnit?.facilities?.length || 0} selected
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab 3: Select Parties */}
          {activeTab === 3 && (
            <div className="tab-panel">
              <h3 className="tab-title">Select Parties</h3>
              
              {/* Lessor Section */}
              <div className="mb-4">
                {!propertyId ? (
                  <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle"></i> Please select a property first.
                  </div>
                ) : lessor?.propertyOwnershipId ? (
                  (() => {
                    const selectedOwnership = propertyOwnerships.find(
                      o => (o.id || o.Id) === lessor.propertyOwnershipId
                    );
                    if (!selectedOwnership) {
                      return (
                        <div className="alert alert-warning">
                          <i className="fas fa-exclamation-triangle"></i> Selected lessor not found.
                        </div>
                      );
                    }
                    return (
                      <div className="lessor-display-card" style={{ 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '8px', 
                        padding: '1.5rem',
                        backgroundColor: '#fff'
                      }}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h6 style={{ 
                              fontSize: '1.1rem', 
                              fontWeight: 600, 
                              color: '#333',
                              marginBottom: '1rem'
                            }}>
                              {selectedOwnership.ownershipNameEng || selectedOwnership.OwnershipNameEng || 'N/A'}
                            </h6>
                            <div className="d-flex flex-wrap gap-3 align-items-center" style={{ marginBottom: '0.75rem' }}>
                              <span className="badge" style={{ 
                                backgroundColor: '#bf4f1f', 
                                color: 'white', 
                                padding: '0.375rem 0.75rem',
                                borderRadius: '20px',
                                fontSize: '0.875rem',
                                fontWeight: 500
                              }}>
                                Lessor
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : propertyOwnerships.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle"></i> No property ownerships available. Click "Add New Lessor" to create one.
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>NAME (ENGLISH)</th>
                          <th>NAME (ARABIC)</th>
                          <th>IDENTITY NUMBER</th>
                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {propertyOwnerships.map((ownership) => {
                          const ownershipId = ownership.id || ownership.Id;
                          return (
                            <tr 
                              key={ownershipId}
                            >
                              <td>{ownership.ownershipNameEng || ownership.OwnershipNameEng || 'N/A'}</td>
                              <td>{ownership.ownershipNameAr || ownership.OwnershipNameAr || '-'}</td>
                              <td>{ownership.identityNumber || ownership.IdentityNumber || '-'}</td>
                              <td>-</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Second Party - Organization */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-building"></i> Second Party (Organization)
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-building"></i> Select Organization <span className="text-danger">*</span>
                    </label>
                    <div className="input-wrapper">
                      <i className="fas fa-building input-icon"></i>
                      <select
                        className="form-select"
                        value={organizationId}
                        onChange={() => {}}
                        required
                        disabled
                      >
                        <option value="">-- Select Organization --</option>
                        {organizations.map(org => (
                          <option key={org.id || org.Id} value={org.id || org.Id}>
                            {org.organizationNameEng || org.OrganizationNameEng || org.organizationNameAr || org.OrganizationNameAr} 
                            {(org.organizationCode || org.OrganizationCode) && ` - ${org.organizationCode || org.OrganizationCode}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 4: Agreement Scope */}
          {activeTab === 4 && (
            <div className="tab-panel">
              <h3 className="tab-title">Agreement Scope</h3>
              <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-list-check"></i> Select Agreement Scope
                  </h6>
                </div>
                <div className="card-body">
                  <div className="facilities-section-container">
                    <div className="row g-3" style={{ width: '100%', margin: 0 }}>
                      {scopeOptions.map(scope => {
                        const isSelected = selectedScopes.includes(scope.value);
                        return (
                          <div key={scope.value} className="col-md-4" style={{ padding: '0.5rem' }}>
                            <div
                              className={`facility-card contract-unit-facility-card ${isSelected ? 'active' : ''}`}
                              onClick={() => {}}
                              style={{ cursor: 'default', opacity: isSelected ? 1 : 0.5 }}
                            >
                              <div className="facility-card-content">
                                <div className="facility-icon">
                                  <i className="fas fa-check-circle"></i>
                                </div>
                                <div className="facility-info">
                                  <div className="facility-name">{scope.label}</div>
                                </div>
                                {isSelected && (
                                  <div className="facility-quantity-select">
                                    <i className="fas fa-check text-success" style={{ fontSize: '1.2rem' }}></i>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 5: Additional & Terms */}
          {activeTab === 5 && (
            <div className="tab-panel">
              <h3 className="tab-title">Additional & Terms</h3>
              
              {/* Default Terms Section (Read-Only) */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-file-contract"></i> Default Terms (Read-Only)
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                    {/* Arabic Section */}
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> Default Terms (Arabic)
                      </label>
                      <textarea
                        className="form-input"
                        value={defaultTermsAr}
                        readOnly
                        style={{ 
                          minHeight: '400px', 
                          resize: 'vertical',
                          backgroundColor: '#f8f9fa',
                          cursor: 'not-allowed'
                        }}
                      />
                    </div>
                    {/* English Section */}
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> Default Terms (English)
                      </label>
                      <textarea
                        className="form-input"
                        value={defaultTermsEng}
                        readOnly
                        style={{ 
                          minHeight: '400px', 
                          resize: 'vertical',
                          backgroundColor: '#f8f9fa',
                          cursor: 'not-allowed'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Terms Section (Editable) */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 style={{ color: '#bf4f1f', fontWeight: 600 }}>
                    <i className="fas fa-plus-circle"></i> Additional Terms (Editable)
                  </h6>
                </div>
                <div className="card-body">
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                    {/* Arabic Section */}
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> Additional Terms (Arabic)
                      </label>
                      <textarea
                        className="form-input"
                        value={additionalTermsAr}
                        onChange={() => {}}
                        placeholder="Enter additional terms in Arabic..."
                        style={{ minHeight: '300px', resize: 'vertical' }}
                        disabled
                        readOnly
                      />
                    </div>
                    {/* English Section */}
                    <div className="form-group">
                      <label className="form-label">
                        <i className="fas fa-language"></i> Additional Terms (English)
                      </label>
                      <textarea
                        className="form-input"
                        value={additionalTermsEng}
                        onChange={() => {}}
                        placeholder="Enter additional terms in English..."
                        style={{ minHeight: '300px', resize: 'vertical' }}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation - View Mode: Just tabs, no navigation buttons */}
        <div className="tab-navigation" style={{ justifyContent: 'center', padding: '1rem' }}>
          <span className="text-muted">
            <i className="fas fa-info-circle"></i> View Mode - All tabs are read-only
          </span>
        </div>
      </div>

      {/* Modals removed in view mode */}
      {false && showAddLessorModal && propertyId && (
        <div className="modal-overlay" onClick={() => setShowAddLessorModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-user-plus"></i> Add New Lessor
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowAddLessorModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> Name Ar <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-language input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.nameAr}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, nameAr: e.target.value })}
                      placeholder="Enter Arabic name"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-language"></i> Name Eng <span className="text-danger">*</span>
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-language input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.nameEng}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, nameEng: e.target.value })}
                      placeholder="Enter English name"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-calendar"></i> Date of Birth
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-calendar input-icon"></i>
                    <input
                      type="date"
                      className="form-input"
                      value={lessorFormData.dob}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, dob: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-flag"></i> Nationality
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-flag input-icon"></i>
                    <select
                      className="form-select"
                      value={lessorFormData.nationalityId}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, nationalityId: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id || nat.Id} value={nat.id || nat.Id}>
                          {nat.nationalityNameEng || nat.NationalityNameEng || nat.nationalityNameAr || nat.NationalityNameAr}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card"></i> Identity Type
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-card input-icon"></i>
                    <select
                      className="form-select"
                      value={lessorFormData.identityTypeId}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, identityTypeId: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {(dropdowns.identities || []).map(identity => (
                        <option key={identity.id || identity.Id} value={identity.id || identity.Id}>
                          {identity.identityNameEng || identity.IdentityNameEng || identity.identityNameAr || identity.IdentityNameAr}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-badge"></i> Identity Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-id-badge input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.identityNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, identityNumber: e.target.value })}
                      placeholder="Enter identity number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-flag"></i> Mobile Country Code
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-flag input-icon"></i>
                    <select
                      className="form-select"
                      value={lessorFormData.mobileCountryCode}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, mobileCountryCode: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {(dropdowns.nationalities || []).map(nat => (
                        <option key={nat.id || nat.Id} value={nat.phoneCode || nat.PhoneCode || ''}>
                          {nat.nationalityNameEng || nat.NationalityNameEng} ({nat.phoneCode || nat.PhoneCode || ''})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-phone"></i> Mobile Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-phone input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.mobileNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, mobileNumber: e.target.value })}
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-envelope"></i> Email
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      className="form-input"
                      value={lessorFormData.emailId}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, emailId: e.target.value })}
                      placeholder="Enter email address"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-university"></i> Bank
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-university input-icon"></i>
                    <select
                      className="form-select"
                      value={lessorFormData.bankId}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, bankId: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {(dropdowns.banks || []).map(bank => (
                        <option key={bank.id || bank.Id} value={bank.id || bank.Id}>
                          {bank.nameEng || bank.NameEng || bank.nameAr || bank.NameAr}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-credit-card"></i> Bank Account Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-credit-card input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.bankAccountNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, bankAccountNumber: e.target.value })}
                      placeholder="Enter bank account number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marked-alt"></i> Area
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map-marked-alt input-icon"></i>
                    <select
                      className="form-select"
                      value={lessorFormData.areaId}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, areaId: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {(dropdowns.areas || []).map(area => (
                        <option key={area.id || area.Id} value={area.id || area.Id}>
                          {area.areaNameEng || area.AreaNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-city"></i> City
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-city input-icon"></i>
                    <select
                      className="form-select"
                      value={lessorFormData.cityId}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, cityId: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {(dropdowns.cities || []).map(city => (
                        <option key={city.id || city.Id} value={city.id || city.Id}>
                          {city.cityNameEng || city.CityNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map"></i> District
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-map input-icon"></i>
                    <select
                      className="form-select"
                      value={lessorFormData.districtId}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, districtId: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {(dropdowns.districts || []).map(district => (
                        <option key={district.id || district.Id} value={district.id || district.Id}>
                          {district.districtNameEng || district.DistrictNameEng}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-road"></i> Street Name
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-road input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.streetName}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, streetName: e.target.value })}
                      placeholder="Enter street name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-building"></i> Building Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-building input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.buildingNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, buildingNumber: e.target.value })}
                      placeholder="Enter building number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-plus"></i> Additional Number
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-plus input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.additionalNumber}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, additionalNumber: e.target.value })}
                      placeholder="Enter additional number"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-mail-bulk"></i> Postal Code
                  </label>
                  <div className="input-wrapper">
                    <i className="fas fa-mail-bulk input-icon"></i>
                    <input
                      type="text"
                      className="form-input"
                      value={lessorFormData.postalCode}
                      onChange={(e) => setLessorFormData({ ...lessorFormData, postalCode: e.target.value })}
                      placeholder="Enter postal code"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAddLessorModal(false)}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  if (!propertyId) {
                    setErrorMessage('Please select a property first.');
                    setShowErrorModal(true);
                    return;
                  }

                  if (!lessorFormData.nameAr || !lessorFormData.nameEng) {
                    setErrorMessage('Arabic and English names are required.');
                    setShowErrorModal(true);
                    return;
                  }

                  try {
                    const ownershipData = {
                      propertyId: propertyId,
                      ownershipNameAr: lessorFormData.nameAr,
                      ownershipNameEng: lessorFormData.nameEng,
                      dob: lessorFormData.dob || null,
                      nationalityId: lessorFormData.nationalityId ? parseInt(lessorFormData.nationalityId) : null,
                      identityTypeId: lessorFormData.identityTypeId ? parseInt(lessorFormData.identityTypeId) : null,
                      identityNumber: lessorFormData.identityNumber || null,
                      mobileNumberCountryCode: lessorFormData.mobileCountryCode || null,
                      mobileNumber: lessorFormData.mobileNumber || null,
                      emailId: lessorFormData.emailId || null,
                      bankId: lessorFormData.bankId ? parseInt(lessorFormData.bankId) : null,
                      bankAccountNumber: lessorFormData.bankAccountNumber || null,
                      areaId: lessorFormData.areaId ? parseInt(lessorFormData.areaId) : null,
                      cityId: lessorFormData.cityId ? parseInt(lessorFormData.cityId) : null,
                      districtId: lessorFormData.districtId ? parseInt(lessorFormData.districtId) : null,
                      streetName: lessorFormData.streetName || null,
                      buildingNumber: lessorFormData.buildingNumber || null,
                      additionalNumber: lessorFormData.additionalNumber || null,
                      postalCode: lessorFormData.postalCode || null,
                      isAgent: false,
                      agentNumber: null,
                      ownerId: null
                    };

                    const createdOwnership = await createPropertyOwnership(ownershipData);
                    await loadPropertyOwnerships(propertyId);
                    setLessor({ propertyOwnershipId: createdOwnership.id || createdOwnership.Id });
                    setSuccessMessage('Lessor created successfully.');
                    setShowSuccessModal(true);
                    setShowAddLessorModal(false);
                    setLessorFormData({
                      agentNumber: '',
                      nameAr: '',
                      nameEng: '',
                      dob: '',
                      nationalityId: '',
                      identityTypeId: '',
                      identityNumber: '',
                      mobileCountryCode: '',
                      mobileNumber: '',
                      emailId: '',
                      bankId: '',
                      bankAccountNumber: '',
                      areaId: '',
                      cityId: '',
                      districtId: '',
                      streetName: '',
                      buildingNumber: '',
                      additionalNumber: '',
                      postalCode: ''
                    });
                  } catch (err) {
                    console.error('Failed to create lessor:', err);
                    setErrorMessage(err.response?.data?.message || 'Failed to create lessor. Please try again.');
                    setShowErrorModal(true);
                  }
                }}
              >
                <i className="fas fa-save"></i> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals removed in view mode */}
      {false && showUnitFacilitiesModal && (
        <div className="modal-overlay" onClick={() => setShowUnitFacilitiesModal(false)}>
          <div className="modal-content modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-star"></i> Select Facilities and Amenities
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowUnitFacilitiesModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="facilities-section-container">
                <div className="row g-3" style={{ width: '100%', margin: 0 }}>
                  {(dropdowns.unitFacilities || []).map(facility => {
                    const facilityId = facility.id || facility.Id;
                    const isSelected = selectedUnitFacilities[facilityId] !== undefined;
                    const quantity = selectedUnitFacilities[facilityId] || 1;
                    return (
                      <div key={facilityId} className="col-md-3" style={{ padding: '0.5rem' }}>
                        <div
                          className={`facility-card contract-unit-facility-card ${isSelected ? 'active' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              const updated = { ...selectedUnitFacilities };
                              delete updated[facilityId];
                              setSelectedUnitFacilities(updated);
                            } else {
                              setSelectedUnitFacilities({ ...selectedUnitFacilities, [facilityId]: 1 });
                            }
                          }}
                        >
                          <div className="facility-card-content">
                            <div className="facility-icon">
                              {facility.icon || facility.Icon ? (
                                <img src={facility.icon || facility.Icon} alt={facility.nameEng || facility.NameEng} />
                              ) : (
                                <i className="fas fa-check-circle"></i>
                              )}
                            </div>
                            <div className="facility-info">
                              <div className="facility-name">{facility.nameEng || facility.NameEng}</div>
                              {(facility.nameAr || facility.NameAr) && (
                                <div className="facility-name-ar">{facility.nameAr || facility.NameAr}</div>
                              )}
                            </div>
                            <div className="facility-quantity-select">
                              <select
                                className="form-select form-select-sm"
                                value={quantity}
                                disabled={!isSelected}
                                onChange={(e) => {
                                  setSelectedUnitFacilities({
                                    ...selectedUnitFacilities,
                                    [facilityId]: parseInt(e.target.value) || 1
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {[...Array(20)].map((_, i) => (
                                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary-custom" onClick={() => setShowUnitFacilitiesModal(false)}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary-custom"
                onClick={() => {
                  if (currentUnitIndex >= 0 && currentUnitIndex < selectedUnits.length) {
                    const updated = [...selectedUnits];
                    updated[currentUnitIndex] = {
                      ...updated[currentUnitIndex],
                      facilities: Object.keys(selectedUnitFacilities).map(facilityId => ({
                        FacilityId: parseInt(facilityId),
                        Quantity: selectedUnitFacilities[facilityId]
                      }))
                    };
                    setSelectedUnits(updated);
                  }
                  setShowUnitFacilitiesModal(false);
                  setSelectedUnitFacilities({});
                  setCurrentUnitIndex(-1);
                }}
              >
                <i className="fas fa-save"></i> Save
              </button>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default BrokerageAgreementView;
